import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, ListGroup } from 'react-bootstrap';
import { userService } from '../../services/userService';
import UserTypeBadge from '../../utils/UserTypeBadge';
import Required from '../../utils/Required';

const MAX_SUGGESTIONS = 8;

const AgencyOverrideModal = ({ show, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    username: '',
    agencyEmail: '',
    agencyName: ''
  });

  const [allUsers, setAllUsers] = useState([]); // [{ username, userType }]
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userSelected, setUserSelected] = useState(false);
  const [errors, setErrors] = useState([]);

  const inputRef = useRef(null);

  /* -----------------------------
   * Load username list (once per open)
   * ----------------------------- */
  useEffect(() => {
    if (!show) return;

    const loadUsers = async () => {
      try {
        const response = await userService.getUsernames();

        if (response?.success) {
          let users = [];

          // normalize all possible response shapes
          if (Array.isArray(response.users)) {
            users = response.users;
          } else if (Array.isArray(response.usernames)) {
            users = response.usernames.map(u => ({ username: u }));
          } else if (Array.isArray(response)) {
            users = response.map(u =>
              typeof u === 'string' ? { username: u } : u
            );
          }

          setAllUsers(users);
        }
      } catch (err) {
        console.error('Error loading users:', err);
      }
    };

    loadUsers();
  }, [show]);

  /* -----------------------------
   * Filter suggestions
   * ----------------------------- */
  useEffect(() => {
    if (!formData.username || userSelected) {
      setShowSuggestions(false);
      return;
    }

    if (formData.username.length < 2) {
      setFilteredUsers([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
      return;
    }

    const matches = allUsers
      .filter(u =>
        u.username.toLowerCase().includes(formData.username.toLowerCase())
      )
      .slice(0, MAX_SUGGESTIONS);

    setFilteredUsers(matches);
    setShowSuggestions(matches.length > 0);
    setActiveIndex(-1);
  }, [formData.username, allUsers, userSelected]);

  /* -----------------------------
   * Input handlers
   * ----------------------------- */
  const handleUsernameChange = (e) => {
    setFormData({
      username: e.target.value,
      agencyEmail: '',
      agencyName: ''
    });

    setUserSelected(false);
    setErrors([]);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev =>
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev =>
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;

      case 'Enter': {
        const selected =
          activeIndex >= 0
            ? filteredUsers[activeIndex]
            : filteredUsers.find(
                u => u.username.toLowerCase() === formData.username.toLowerCase()
              );

        if (selected) {
          e.preventDefault();
          selectUser(selected);
        }
        break;
      }

      case 'Escape':
        setShowSuggestions(false);
        setActiveIndex(-1);
        break;

      default:
        break;
    }
  };

  /* -----------------------------
   * Selection logic (keyboard + mouse)
   * ----------------------------- */
  const selectUser = async (userSummary) => {
    setLoadingUser(true);
    setUserSelected(true);          // ✅ permanently suppress suggestions
    setShowSuggestions(false);
    setFilteredUsers([]);
    setActiveIndex(-1);

    try {
      const response = await userService.getUser(userSummary.username);
      if (response?.success) {
        setFormData({
          username: response.user.username,
          agencyEmail: response.user.emailAddress,
          agencyName: response.user.agencyName
        });
      }
    } finally {
      setLoadingUser(false);
    }
  };

  /* -----------------------------
   * Submit
   * ----------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    const result = await onSubmit(formData);

    if (!result?.success) {
      setErrors(result?.validationErrors || [result?.error || 'Failed']);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({ username: '', agencyEmail: '', agencyName: '' });
    setFilteredUsers([]);
    setShowSuggestions(false);
    setActiveIndex(-1);
    setUserSelected(false);
    setErrors([]);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="bg-primary">
        <Modal.Title>Create Benefit on Behalf of User</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>

          {errors.length > 0 && (
            <Alert variant="danger">
              <ul className="mb-0">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Alert>
          )}

          <Form.Group className="mb-3 position-relative">
            <Form.FloatingLabel label={<Required>Username</Required>} controlId="username">
            <Form.Control
              ref={inputRef}
              type="text"
              value={formData.username}
              onChange={handleUsernameChange}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              required
              placeholder=""
            />

            {showSuggestions && (
              <ListGroup
                className="position-absolute w-100 shadow"
                style={{ zIndex: 1000 }}
              >
                {filteredUsers.map((u, idx) => (
                  <ListGroup.Item
                    key={u.username}
                    action
                    active={idx === activeIndex}
                    onMouseDown={() => selectUser(u)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{u.username}</span>
                      {u.userType != null && (
                        <UserTypeBadge userType={u.userType} userAgency={u.agencyName}/>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            </Form.FloatingLabel>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.FloatingLabel label="Agency Email" controlId="agency-email">
              <Form.Control value={formData.agencyEmail} placeholder="" readOnly disabled/>
            </Form.FloatingLabel>
            <Form.Text>Auto-populated upon selection of username.</Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.FloatingLabel label="Agency Name" controlId="agency-name">
              <Form.Control value={formData.agencyName} placeholder="" readOnly disabled/>
            </Form.FloatingLabel>
            <Form.Text>Auto-populated upon selection of username.</Form.Text>
          </Form.Group>

        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!formData.agencyEmail || loadingUser}
          >
            {loadingUser ? 'Loading…' : 'Set User'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AgencyOverrideModal;